import React, {useCallback, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Link, NavLink, useLocation, useNavigate} from "react-router-dom";
import {
    ADMIN_ROUTE,
    CATALOG_ROUTE,
    LAYOUT_SHELL_CLASS,
    LOGIN_ROUTE,
    ORDERS_ROUTE,
    PROFILE_ROUTE,
    ROLE_ADMIN,
    shopCategoryPath,
} from "../../util/Constants";
import {Context} from "../../index";
import {observer} from "mobx-react-lite";
import {fetchDataUnauth} from "../../http/itemsAPI";
import {CATEGORIES} from "../../http/ApiEndpoints";
import {IconCart, IconUser} from "../../components/UI/NavIcons";
import CartModal from "../../components/cart/CartModal";
import siteLogo from "../../assets/lutica-site-logo.png";

const MOBILE_MAX = 639;
const NAV_ROW_GAP_PX = 32;

function useIsMobileViewport() {
    const [narrow, setNarrow] = useState(
        () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX,
    );
    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
        const sync = () => setNarrow(mq.matches);
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);
    return narrow;
}

const NavBar = observer(() => {
    const {user, items, cart} = useContext(Context);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobileViewport = useIsMobileViewport();
    const [menuOpen, setMenuOpen] = useState(false);
    const [siteNavOpen, setSiteNavOpen] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    const [logoFailed, setLogoFailed] = useState(false);
    const [navOverflowCompact, setNavOverflowCompact] = useState(false);
    const menuRef = useRef(null);
    const siteNavRef = useRef(null);
    const rowRef = useRef(null);
    const logoRef = useRef(null);
    const actionsRef = useRef(null);
    const hamburgerRef = useRef(null);
    const desktopNavRef = useRef(null);
    const cartAnchorRef = useRef(null);

    const showHamburger = isMobileViewport || navOverflowCompact;
    const showDesktopInlineNav = !isMobileViewport && !navOverflowCompact;

    useEffect(() => {
        if (items.categories.length === 0)
            fetchDataUnauth(CATEGORIES).then(data => items.setCategories(data));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- store stable; однократная подгрузка
    }, []);

    useEffect(() => {
        cart.refreshTotals(user.isAuth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.isAuth]);

    useEffect(() => {
        const close = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
            if (siteNavRef.current && !siteNavRef.current.contains(e.target)) {
                setSiteNavOpen(false);
            }
        };
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, []);

    useEffect(() => {
        setSiteNavOpen(false);
        setCartOpen(false);
    }, [location.pathname, location.hash]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setSiteNavOpen(false);
                setMenuOpen(false);
                setCartOpen(false);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    const measureNavOverflow = useCallback(() => {
        if (typeof window === 'undefined' || window.innerWidth <= MOBILE_MAX) {
            setNavOverflowCompact(false);
            return;
        }
        const row = rowRef.current;
        const nav = desktopNavRef.current;
        const logoEl = logoRef.current;
        const actionsEl = actionsRef.current;
        if (!row || !nav || !logoEl || !actionsEl) {
            return;
        }
        const neededWidth = nav.scrollWidth;
        const totalIfInline =
            logoEl.offsetWidth + neededWidth + actionsEl.offsetWidth + NAV_ROW_GAP_PX;
        setNavOverflowCompact(totalIfInline > row.clientWidth + 2);
    }, []);

    useLayoutEffect(() => {
        measureNavOverflow();
    }, [measureNavOverflow, items.categories, user.isAuth, navOverflowCompact]);

    useEffect(() => {
        const row = rowRef.current;
        if (!row || typeof ResizeObserver === 'undefined') {
            return;
        }
        const ro = new ResizeObserver(() => {
            window.requestAnimationFrame(measureNavOverflow);
        });
        ro.observe(row);
        return () => ro.disconnect();
    }, [measureNavOverflow]);

    const logout = () => {
        user.logout();
        setMenuOpen(false);
        navigate(LOGIN_ROUTE);
    };

    /** Главная + сброс якоря (#delivery): иначе RR не меняет URL и страница остаётся у блока «Доставка». */
    const goHomeFromLogo = (e) => {
        e.preventDefault();
        navigate(
            {pathname: CATALOG_ROUTE, hash: ''},
            {replace: location.pathname === CATALOG_ROUTE},
        );
        window.setTimeout(() => {
            window.scrollTo({top: 0, behavior: 'smooth'});
        }, 0);
    };

    const linkClass =
        'inline-flex min-h-10 items-center rounded-full px-3 py-2 text-sm text-cream-100/90 transition hover:bg-white/10 hover:text-white';

    const mobileNavLinkClass =
        'flex min-h-11 w-full items-center rounded-xl px-3 py-2.5 text-left text-sm text-cream-100/95 transition hover:bg-white/10 active:bg-white/15';

    const badge =
        cart.totalPieces > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-mustard-400 px-1 text-[10px] font-bold leading-none text-bark-900 shadow-sm">
                {cart.totalPieces > 99 ? '99+' : cart.totalPieces}
            </span>
        ) : null;

    const navLinks = (
        <>
            {items.categories.map((category) => (
                <Link
                    key={category.title}
                    to={shopCategoryPath(category.title)}
                    className={`${linkClass} shrink-0 touch-manipulation whitespace-nowrap`}
                >
                    {category.title}
                </Link>
            ))}
            <Link
                to={{pathname: CATALOG_ROUTE, hash: 'delivery'}}
                className={`${linkClass} shrink-0 touch-manipulation whitespace-nowrap`}
            >
                Доставка
            </Link>
        </>
    );

    return (
        <>
            <header
                className="fixed left-0 right-0 top-0 z-50 border-b border-pine-800/30 bg-pine-900/90 pt-[env(safe-area-inset-top,0px)] shadow-sm backdrop-blur-xl">
                <div ref={siteNavRef} className="relative">
                    <div
                        ref={rowRef}
                        className={`${LAYOUT_SHELL_CLASS} relative flex min-h-[3.5rem] items-center gap-2 py-1 sm:min-h-16 sm:gap-4 sm:py-0 ${
                            showHamburger ? 'justify-between' : 'justify-start'
                        }`}
                    >
                        <button
                            ref={hamburgerRef}
                            type="button"
                            className={`inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full border border-cream-200/25 bg-white/5 text-cream-50 transition touch-manipulation hover:bg-white/10 ${
                                showHamburger ? '' : 'hidden'
                            }`}
                            aria-label={siteNavOpen ? 'Закрыть меню' : 'Открыть меню'}
                            aria-expanded={siteNavOpen}
                            aria-controls="site-nav-mobile-panel"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSiteNavOpen((o) => !o);
                            }}
                        >
                            {siteNavOpen ? (
                                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                                    <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                                </svg>
                            ) : (
                                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                                    <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
                                </svg>
                            )}
                        </button>
                        <NavLink
                            ref={logoRef}
                            className={`flex min-h-11 shrink-0 items-center text-lg font-semibold tracking-tight text-white touch-manipulation ${
                                showHamburger
                                    ? `absolute top-1/2 z-[1] -translate-y-1/2 ${
                                          isMobileViewport
                                              ? 'left-[45%] -translate-x-1/2'
                                              : 'left-1/2 -translate-x-1/2'
                                      }`
                                    : ''
                            }`}
                            to={CATALOG_ROUTE}
                            onClick={goHomeFromLogo}
                        >
                            {!logoFailed ? (
                                <img
                                    src={siteLogo}
                                    alt="Лютика"
                                    className="h-6 w-auto max-h-7 max-w-[7rem] object-contain sm:h-7 sm:max-w-[7.25rem]"
                                    onError={() => setLogoFailed(true)}
                                />
                            ) : (
                                <span className="whitespace-nowrap tracking-wide">ЛЮТИКА</span>
                            )}
                        </NavLink>
                        {!isMobileViewport && (
                            <nav
                                ref={desktopNavRef}
                                className={
                                    showDesktopInlineNav
                                        ? 'flex min-h-10 min-w-0 flex-1 items-center gap-0.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-1 [&::-webkit-scrollbar]:hidden'
                                        : 'pointer-events-none absolute -left-[9999px] top-0 flex w-max flex-nowrap items-center gap-0.5 opacity-0 sm:gap-1'
                                }
                                aria-hidden={!showDesktopInlineNav}
                                aria-label="Основное меню"
                            >
                                {navLinks}
                            </nav>
                        )}
                        <div
                            ref={actionsRef}
                            className={`flex shrink-0 items-center gap-1 sm:gap-2 ${showHamburger ? '' : 'ml-auto'}`}
                        >
                            <div ref={cartAnchorRef} className="relative">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSiteNavOpen(false);
                                        setCartOpen((o) => !o);
                                    }}
                                    className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-cream-200/25 bg-white/5 text-cream-50 transition touch-manipulation hover:bg-white/10"
                                    aria-expanded={cartOpen}
                                    aria-haspopup="dialog"
                                    aria-label={`Корзина${cart.totalPieces > 0 ? `, ${cart.totalPieces} шт.` : ''}`}
                                    title="Корзина"
                                >
                                    <IconCart className="h-5 w-5"/>
                                </button>
                                {badge}
                            </div>
                            {!user.isAuth && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSiteNavOpen(false);
                                        navigate(LOGIN_ROUTE);
                                    }}
                                    className="lut-btn-mustard min-h-11 px-4 py-2 text-sm touch-manipulation"
                                >
                                    Вход
                                </button>
                            )}
                            {user.isAuth && (
                                <div className="relative" ref={menuRef}>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSiteNavOpen(false);
                                            setMenuOpen((o) => !o);
                                        }}
                                        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-cream-200/25 bg-white/5 text-cream-50 transition touch-manipulation hover:bg-white/10"
                                        aria-label="Меню профиля"
                                        aria-expanded={menuOpen}
                                        title="Профиль"
                                    >
                                        <IconUser className="h-5 w-5"/>
                                    </button>
                                    {menuOpen && (
                                        <div
                                            className="absolute right-0 mt-2 w-48 overflow-hidden rounded-2xl border border-stone-200/80 bg-white py-1 shadow-soft">
                                            <button type="button" className="block w-full px-4 py-2.5 text-left text-sm text-bark-700 hover:bg-cream-100"
                                                    onClick={() => { setMenuOpen(false); navigate(PROFILE_ROUTE) }}>Профиль</button>
                                            <button type="button" className="block w-full px-4 py-2.5 text-left text-sm text-bark-700 hover:bg-cream-100"
                                                    onClick={() => { setMenuOpen(false); navigate(ORDERS_ROUTE) }}>Заказы</button>
                                            {user.userRole === ROLE_ADMIN && (
                                                <button type="button" className="block w-full px-4 py-2.5 text-left text-sm text-bark-700 hover:bg-cream-100"
                                                        onClick={() => { setMenuOpen(false); navigate(ADMIN_ROUTE) }}>Админ</button>
                                            )}
                                            <button type="button" className="block w-full border-t border-stone-100 px-4 py-2.5 text-left text-sm text-red-700 hover:bg-red-50"
                                                    onClick={logout}>Выйти</button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {siteNavOpen && showHamburger && (
                        <div
                            className="absolute left-3 top-full z-40 max-h-[min(72vh,calc(100dvh-4rem))] w-[min(20rem,calc(100vw-1.5rem))] overflow-y-auto rounded-b-2xl border border-pine-700/40 border-t-0 bg-pine-900/95 shadow-xl backdrop-blur-xl"
                            id="site-nav-mobile-panel"
                            role="region"
                            aria-label="Разделы магазина"
                        >
                            <nav
                                className="flex flex-col gap-0.5 px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
                                aria-label="Категории и разделы"
                            >
                                {items.categories.map((category) => (
                                    <Link
                                        key={category.title}
                                        to={shopCategoryPath(category.title)}
                                        className={mobileNavLinkClass}
                                        onClick={() => setSiteNavOpen(false)}
                                    >
                                        {category.title}
                                    </Link>
                                ))}
                                <Link
                                    to={{pathname: CATALOG_ROUTE, hash: 'delivery'}}
                                    className={mobileNavLinkClass}
                                    onClick={() => setSiteNavOpen(false)}
                                >
                                    Доставка
                                </Link>
                            </nav>
                        </div>
                    )}
                </div>
            </header>
            <CartModal open={cartOpen} onClose={() => setCartOpen(false)} anchorRef={cartAnchorRef}/>
        </>
    );
});

export default NavBar;
